import { useMemo } from 'react'
import { IClientInfo } from '@common/interfaces/client'
import { PhotoSubjectType } from '@common/interfaces/photo'
import ProfilePhoto from '../ProfilePhoto'

// The subset of user fields the avatar actually reads. Accepting this (rather
// than the full IUser) lets lighter refs — e.g. a CRM owner ref — render an
// avatar too. A full IUser is assignable to it. Arc (the bot user) renders its
// brand mark like any other user: its `avatar` column points at the hosted icon
// (see the bot-user avatar migration), so no bot-specific branch is needed here.
export interface IAvatarUser {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  avatar?: string | null
  googleAvatarUrl?: string | null
}

interface IProps {
  user?: IAvatarUser
  clientInfo?: Pick<IClientInfo, 'iconUrl' | 'clientName'> & { id?: string | null }
  size?: 'small' | 'large'
  avatarClassNameCustom?: string
}

/**
 * A user's or client's avatar.
 *
 * Kept as a thin adapter over Common/ProfilePhoto rather than its own renderer: the two used to
 * disagree about initials and color (this one produced a uniform grey disc for everyone, since it
 * never set a background), and 20+ call sites pass this prop shape. ProfilePhoto owns the
 * behavior now; this maps the legacy props onto it.
 *
 * Prefer ProfilePhoto directly in new code — it takes `subjectType`/`id` and so can also fetch a
 * photo that isn't already in the payload.
 */
const AvatarComponent = ({ user, clientInfo, size = 'large', avatarClassNameCustom }: IProps) => {
  // The legacy sizes: `large` is MUI's default 40px, `small` the 24px --size-xs.
  const photoSize = size === 'small' ? 'small' : 'large'

  const userName = useMemo(
    () => (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : ''),
    [user],
  )

  if (clientInfo) {
    return (
      <ProfilePhoto
        name={clientInfo.clientName}
        iconUrl={clientInfo.iconUrl}
        // A client's own uploaded logo lives under the `clients` subject, so a client that has
        // one renders it in place of the Logo.dev mark without the caller fetching anything.
        subjectType={PhotoSubjectType.Clients}
        id={clientInfo.id}
        size={photoSize}
        className={avatarClassNameCustom}
      />
    )
  }

  if (!user) {
    return null
  }

  return (
    <ProfilePhoto
      name={userName}
      subjectType={user.id ? PhotoSubjectType.Users : undefined}
      id={user.id}
      // Passed as the FALLBACK, not as `photo`: an uploaded S3 photo outranks both columns
      // (see the precedence in server users/helpers.ts resolveUserAvatar), and handing a column
      // value in as the resolved photo is what made uploaded photos invisible for accounts that
      // also had a legacy URL. As a fallback it just spares the first paint a round trip.
      iconUrl={user.avatar || user.googleAvatarUrl}
      size={photoSize}
      className={avatarClassNameCustom}
    />
  )
}

export default AvatarComponent
